import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, ChevronDown, ChevronRight, Filter, UserPlus, Hand,
  Mic, MicOff, Video, VideoOff, MoreVertical, Crown, Shield, Star,
  UserMinus, MessageSquare, Users, MonitorUp, EyeOff, Eye, ShieldPlus, ShieldMinus,
  Clock, ArrowUp, X, Check, Megaphone, Send, Plus, Trash2, Volume2, VolumeX, Settings
} from 'lucide-react';
import { useMeeting, useParticipant, usePubSub } from '../../../config/sdkConfig';
import { useMeetingAppContext } from '../../../MeetingAppContextDef';
import { useSiteContext } from '../../../context/SiteContext';
import { useMeetingState } from '../../../context/MeetingStateContext';
import { useAgoraRTM } from '../../../context/AgoraRTMContext';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';
import { toast } from 'react-toastify';
import { stringToNumericUid } from '../../../context/AgoraContext';
import * as meetingServices from '../../../services/meetingServices';
import { updateMeetingRoom } from '../../../services/roomServices';
import requests from '../../../services/httpServices';

// Generate random color based on name
function getColorFromName(name) {
  const colors = ['#00c853', '#6a1fbf', '#f59e0b', '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
}

// Role config with colors matching prototype
const ROLE_CONFIG = {
  host: { label: 'Host', iconColor: 'text-yellow-400', icon: Crown },
  'co-host': { label: 'Co-Host', iconColor: 'text-orange-400', icon: Shield },
  presenter: { label: 'Presenter', iconColor: 'text-blue-400', icon: Star },
  attendee: { label: 'Attendee', iconColor: 'text-gray-400', icon: null },
};

// Map backend role to display role
function getDisplayRole(backendRole) {
  switch (backendRole) {
    case 'moderator':
      return 'host';
    case 'co-host':
      return 'co-host';
    case 'presenter':
    case 'speaker':
      return 'presenter';
    case 'participant':
    default:
      return 'attendee';
  }
}

// Individual participant item component
function ParticipantItem({ participantId, isLocal, localParticipant, hasRaisedHand, onLowerHand, onRequestUnmute, onMakePresenter, onToggleShadowBan, isShadowBanned, isCoHost, onToggleCoHost, isHostUser, localUserRole, participantRole, onDisableWebcam, onMuteMic, onKickParticipant, isPresenter, onSpotlight, isSpotlighted, onToggleLocalMic, onToggleLocalCamera, categories, participantCategory, onAssignCategory, networkQuality, canSpotlight }) {
  const { displayName, webcamOn, micOn, isActiveSpeaker, disableMic, disableWebcam, pin, unpin, pinState, micStream } = useParticipant(participantId);
  const { recordingStartedById } = useMeetingAppContext();
  // Match the recorder against this row. Tricky because the IDs come from
  // different namespaces:
  //   - recordingStartedById = Mongo userId (string, from RTM senderId or
  //     the local handleConfirmStartRecording explicit set)
  //   - participantId for LOCAL = Mongo userId (string)
  //   - participantId for REMOTE = Agora numeric UID
  // So we try direct string match first (covers local + same-namespace cases),
  // then derive the Agora UID from the recorder's string ID via the same
  // hash used at join time and try that too — that's how we connect a
  // remote row's numeric UID back to the publisher's string userId.
  let isRecorder = false;
  if (recordingStartedById != null) {
    const recIdStr = String(recordingStartedById);
    if (
      recIdStr === String(participantId) ||
      (isLocal && recIdStr === String(localParticipant?.id))
    ) {
      isRecorder = true;
    } else {
      // Derive the Agora UID the recorder would have at join time and
      // compare against this row's numeric participantId.
      try {
        const derivedUid = stringToNumericUid(recIdStr);
        if (String(derivedUid) === String(participantId)) isRecorder = true;
      } catch (_) { /* ignore */ }
    }
  }
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showCategorySubmenu, setShowCategorySubmenu] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [showVolumeSubmenu, setShowVolumeSubmenu] = useState(false);
  const [volumeSubmenuPosition, setVolumeSubmenuPosition] = useState({ top: 0, left: 0 });
  const [volume, setVolume] = useState(100);
  const menuBtnRef = useRef(null);
  const dropdownRef = useRef(null);
  const categoryBtnRef = useRef(null);
  const categorySubmenuRef = useRef(null);
  const volumeBtnRef = useRef(null);
  const volumeSubmenuRef = useRef(null);

  // Apply volume to remote participant's Agora audio track
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    try {
      const agoraTrack = micStream?.agoraTrack;
      if (agoraTrack && typeof agoraTrack.setVolume === 'function') {
        agoraTrack.setVolume(newVolume);
      }
    } catch (e) {
      console.warn('[ParticipantItem] Failed to set volume:', e);
    }
  };

  const name = isLocal ? (localParticipant?.displayName || 'You') : (displayName || 'Participant');
  const initials = getInitials(name);

  // Determine role: for local user use meetingRole from context, for remote use metaData or co-host status
  const getParticipantRole = () => {
    if (isLocal) {
      const baseRole = getDisplayRole(localUserRole);
      // If local user is a presenter but not host/co-host, show presenter badge
      if (baseRole === 'attendee' && isPresenter) {
        return 'presenter';
      }
      return baseRole;
    }
    // For remote participants, check their metaData.meetingRole first
    if (participantRole === 'moderator' || participantRole === 'host') {
      return 'host';
    }
    if (participantRole === 'co-host' || isCoHost) {
      return 'co-host';
    }
    // Check if participant has been made a presenter
    if (isPresenter) {
      return 'presenter';
    }
    return 'attendee';
  };
  const role = getParticipantRole();
  const roleConfig = ROLE_CONFIG[role];
  const RoleIcon = roleConfig?.icon;

  // Check if this participant is the breakout room host
  // Uses moderatorId/name match for both local and remote participants
  const { breakoutModeratorId, breakoutModeratorName, currentBreakoutRoom } = useBreakoutRooms();
  const isBreakoutHost = currentBreakoutRoom && (breakoutModeratorId || breakoutModeratorName) && (
    (breakoutModeratorId && String(participantId) === String(breakoutModeratorId)) ||
    (breakoutModeratorName && name.toLowerCase() === breakoutModeratorName.toLowerCase())
  );

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      // Check if click is outside the button, the dropdown menu, AND any submenu portals
      const isOutsideButton = menuBtnRef.current && !menuBtnRef.current.contains(e.target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(e.target);
      const isOutsideSubmenu = !categorySubmenuRef.current || !categorySubmenuRef.current.contains(e.target);
      const isOutsideVolume = !volumeSubmenuRef.current || !volumeSubmenuRef.current.contains(e.target);

      if (isOutsideButton && isOutsideDropdown && isOutsideSubmenu && isOutsideVolume) {
        setShowMenu(false);
        setShowCategorySubmenu(false);
        setShowVolumeSubmenu(false);
      }
    };

    // Add listener with slight delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Handle menu button click with position calculation
  const handleMenuClick = (e) => {
    e.stopPropagation();
    if (showMenu) {
      setShowMenu(false);
      setShowCategorySubmenu(false);
      setShowVolumeSubmenu(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuHeight = 320; // approximate max height of dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < menuHeight;
      setMenuPosition({
        // When flipping up, anchor menu bottom to button top using CSS bottom
        ...(flipUp
          ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
          : { top: rect.bottom + 4, bottom: 'auto' }),
        left: Math.min(rect.left, window.innerWidth - 180)
      });
      setShowMenu(true);
    }
  };

  // Handle mic toggle
  const handleMicToggle = () => {
    if (micOn) {
      // Use PubSub to request remote participant to mute
      onMuteMic?.(participantId);
    } else {
      onRequestUnmute?.(participantId, 'mic');
    }
  };

  // Handle camera toggle
  const handleCameraToggle = () => {
    if (webcamOn) {
      // Use PubSub to request remote participant to disable webcam
      onDisableWebcam?.(participantId);
    } else {
      onRequestUnmute?.(participantId, 'camera');
    }
  };

  return (
    <div className={`group flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors ${isActiveSpeaker ? 'bg-green-500/10 ring-1 ring-green-500/30' : ''
      }`}>
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ${hasRaisedHand ? 'ring-2 ring-yellow-400' : ''} ${isActiveSpeaker ? 'ring-2 ring-green-500 ring-offset-1 ring-offset-[#001233]' : ''}`}
        >
          <div className="w-full h-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
            <span className="text-sm font-bold text-white">{initials}</span>
          </div>
        </div>
        {hasRaisedHand && (
          <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center animate-bounce">
            <Hand className="w-2.5 h-2.5 text-white" />
          </div>
        )}
        {isSpotlighted && !hasRaisedHand && !isShadowBanned && (
          <div className="absolute -top-0.5 -left-0.5 w-4 h-4 bg-purple-500 rounded-full border-2 border-[#001233] flex items-center justify-center">
            <Star className="w-2.5 h-2.5 text-white fill-white" />
          </div>
        )}
        {isShadowBanned && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-orange-500 rounded-full border-2 border-[#001233] flex items-center justify-center">
            <EyeOff className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </div>

      {/* Name and role */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-sm font-medium break-words ${isShadowBanned ? 'text-orange-300' : 'text-white'}`}>
            {name}
            {isLocal && <span className="text-gray-500 ml-1">(You)</span>}
          </span>
          {isRecorder && (
            <span
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-[9px] font-semibold text-red-300"
              title="Started this recording"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
              </span>
              Recording
            </span>
          )}
          {RoleIcon && (
            <RoleIcon className={`w-3 h-3 flex-shrink-0 ${
              role === 'host' ? 'text-yellow-400' :
              role === 'co-host' ? 'text-orange-400' :
              role === 'presenter' ? 'text-blue-400' : 'text-gray-400'
            }`} />
          )}
          {isBreakoutHost && (
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium bg-yellow-400/15 text-yellow-400">
              <Crown className="w-2.5 h-2.5" />
              Room Host
            </span>
          )}
          {participantCategory && (() => {
            const CatIcon = CATEGORY_ICONS[participantCategory.icon] || Crown;
            return (
              <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium" style={{ backgroundColor: `${participantCategory.color}20`, color: participantCategory.color }}>
                <CatIcon className="w-2.5 h-2.5" />
                {participantCategory.name}
              </span>
            );
          })()}
          {isShadowBanned && (
            <EyeOff className="w-3 h-3 text-purple-400 flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Network Quality Indicator - signal bars */}
      {(() => {
        // networkQuality: { uplink: 0-6, downlink: 0-6 } where 1=excellent, 6=very bad, 0=unknown
        const q = networkQuality;
        if (!q) return null;
        const worst = Math.max(q.uplink || 0, q.downlink || 0);
        // 0=unknown, 1=excellent, 2=good — don't show indicator for these
        if (worst <= 2) return null;
        // Map to bars lit: 3(poor)=2 bars yellow, 4(bad)=1 bar orange, 5-6(very bad/disconnected)=1 bar red
        const barsLit = worst <= 3 ? 2 : 1;
        const barColor = worst <= 3 ? 'bg-yellow-400' : worst <= 4 ? 'bg-orange-400' : 'bg-red-400';
        const label = worst <= 3 ? 'Poor connection' : worst <= 4 ? 'Bad connection' : 'Very bad connection';
        return (
          <div className="flex items-end gap-[2px] h-3 flex-shrink-0" title={label}>
            {[1, 2, 3].map((bar) => (
              <div
                key={bar}
                className={`w-[3px] rounded-sm ${bar <= barsLit ? barColor : 'bg-gray-600'}`}
                style={{ height: `${bar * 3 + 1}px` }}
              />
            ))}
          </div>
        );
      })()}

      {/* Status icons - mic/camera pills */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {/* Mic toggle button — hide self-controls for webinar attendees */}
        {isLocal && ["moderator", "co-host", "host", "speaker"].includes(localUserRole) ? (
          <button
            onClick={() => onToggleLocalMic?.()}
            className={`p-1 rounded transition-colors ${micOn ? 'bg-green-500/20' : 'bg-red-500/20'} ${
              micOn ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'
            }`}
            title={micOn ? 'Mute' : 'Unmute'}
          >
            {micOn ? <Mic className="w-3 h-3 text-green-400" /> : <MicOff className="w-3 h-3 text-red-400" />}
          </button>
        ) : (isHostUser || localUserRole === 'co-host') ? (
          <button
            onClick={handleMicToggle}
            className={`p-1 rounded transition-colors ${micOn ? 'bg-green-500/20 hover:bg-white/20' : 'bg-red-500/20 hover:bg-white/20'} cursor-pointer`}
            title={micOn ? 'Mute participant' : 'Request to unmute'}
          >
            {micOn ? <Mic className="w-3 h-3 text-green-400" /> : <MicOff className="w-3 h-3 text-red-400" />}
          </button>
        ) : (
          <div className={`p-1 rounded ${micOn ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
            {micOn ? <Mic className="w-3 h-3 text-green-400" /> : <MicOff className="w-3 h-3 text-red-400" />}
          </div>
        )}

        {/* Camera toggle button — hide self-controls for webinar attendees */}
        {isLocal && ["moderator", "co-host", "host", "speaker"].includes(localUserRole) ? (
          <button
            onClick={() => onToggleLocalCamera?.()}
            className={`p-1 rounded transition-colors ${webcamOn ? 'bg-green-500/20' : 'bg-red-500/20'} ${
              webcamOn ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'
            }`}
            title={webcamOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {webcamOn ? <Video className="w-3 h-3 text-green-400" /> : <VideoOff className="w-3 h-3 text-red-400" />}
          </button>
        ) : (isHostUser || localUserRole === 'co-host') ? (
          <button
            onClick={handleCameraToggle}
            className={`p-1 rounded transition-colors ${webcamOn ? 'bg-green-500/20 hover:bg-white/20' : 'bg-red-500/20 hover:bg-white/20'} cursor-pointer`}
            title={webcamOn ? 'Disable camera' : 'Request to enable camera'}
          >
            {webcamOn ? <Video className="w-3 h-3 text-green-400" /> : <VideoOff className="w-3 h-3 text-red-400" />}
          </button>
        ) : (
          <div className={`p-1 rounded ${webcamOn ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
            {webcamOn ? <Video className="w-3 h-3 text-green-400" /> : <VideoOff className="w-3 h-3 text-red-400" />}
          </div>
        )}
      </div>

      {/* Spotlight toggle — visible inline for host/co-host/presenter on remote participants */}
      {!isLocal && canSpotlight && (
        <button
          onClick={() => onSpotlight?.(participantId, displayName, isSpotlighted)}
          className={`p-1 rounded transition-colors ${
            isSpotlighted
              ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
              : 'text-gray-500 hover:bg-white/10 hover:text-purple-400'
          }`}
          title={isSpotlighted ? 'Remove Spotlight' : 'Spotlight'}
        >
          <Star className={`w-3.5 h-3.5 ${isSpotlighted ? 'fill-purple-400' : ''}`} />
        </button>
      )}

      {/* Actions - More options only visible when current user is host or co-host */}
      {!isLocal && (isHostUser || localUserRole === 'co-host') && (
        <div className="relative" ref={menuBtnRef}>
          <button
            onClick={handleMenuClick}
            className="p-1.5 hover:bg-white/10 rounded transition-colors text-gray-400 hover:text-white"
            title="More options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Dropdown Menu - Rendered via Portal */}
      {showMenu && !isLocal && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-[#1a2744] rounded-lg shadow-2xl border border-white/10 py-1 min-w-40 overflow-y-auto"
          style={{
            top: menuPosition.top,
            bottom: menuPosition.bottom,
            left: menuPosition.left,
            zIndex: 99999,
            maxHeight: 'calc(100vh - 16px)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {hasRaisedHand && (
            <button
              onClick={() => { onLowerHand?.(participantId); setShowMenu(false); }}
              className="w-full px-2.5 py-1.5 text-left text-[11px] text-yellow-400 hover:bg-white/10 flex items-center gap-2"
            >
              <Hand className="w-3 h-3" /> Lower hand
            </button>
          )}
          {micOn ? (
            <button
              onClick={() => { onMuteMic?.(participantId); setShowMenu(false); }}
              className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <MicOff className="w-3 h-3" /> Mute
            </button>
          ) : (
            <button
              onClick={() => { onRequestUnmute?.(participantId, 'mic'); setShowMenu(false); }}
              className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <Mic className="w-3 h-3" /> Request to unmute
            </button>
          )}
          {webcamOn ? (
            <button
              onClick={() => { onDisableWebcam?.(participantId); setShowMenu(false); }}
              className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <VideoOff className="w-3 h-3" /> Disable camera
            </button>
          ) : (
            <button
              onClick={() => { onRequestUnmute?.(participantId, 'camera'); setShowMenu(false); }}
              className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <Video className="w-3 h-3" /> Request to enable camera
            </button>
          )}
          {/* Volume adjustment */}
          <button
            ref={volumeBtnRef}
            onClick={() => {
              if (!showVolumeSubmenu && volumeBtnRef.current) {
                const rect = volumeBtnRef.current.getBoundingClientRect();
                const submenuWidth = 180;
                const spaceRight = window.innerWidth - rect.right;
                const left = spaceRight >= submenuWidth + 4 ? rect.right + 4 : rect.left - submenuWidth - 4;
                let top = rect.top;
                if (top + 60 > window.innerHeight - 8) {
                  top = Math.max(8, window.innerHeight - 68);
                }
                setVolumeSubmenuPosition({ top, left });
              }
              setShowVolumeSubmenu(!showVolumeSubmenu);
              setShowCategorySubmenu(false);
            }}
            className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Volume2 className="w-3 h-3" /> Adjust volume
            </span>
            <span className="text-[10px] text-gray-500">{volume}%</span>
          </button>
          <div className="border-t border-white/10 my-1" />
          <button
            onClick={() => {
              onMakePresenter?.(participantId, displayName);
              setShowMenu(false);
            }}
            className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center gap-2 ${
              isPresenter ? 'text-green-400' : 'text-gray-300'
            }`}
          >
            <MonitorUp className="w-3 h-3" />
            {isPresenter ? 'Remove Presenter' : 'Make Presenter'}
          </button>
          {/* Spotlight — available for host/co-host in webinars */}
          <button
            onClick={() => {
              onSpotlight?.(participantId, displayName, isSpotlighted);
              setShowMenu(false);
            }}
            className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center gap-2 ${
              isSpotlighted ? 'text-purple-400' : 'text-gray-300'
            }`}
          >
            <Star className={`w-3 h-3 ${isSpotlighted ? 'fill-purple-400' : ''}`} />
            {isSpotlighted ? 'Remove Spotlight' : 'Spotlight'}
          </button>
          <button
            onClick={() => {
              onToggleShadowBan?.(participantId, displayName);
              setShowMenu(false);
            }}
            className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center gap-2 ${
              isShadowBanned ? 'text-orange-400' : 'text-gray-300'
            }`}
          >
            {isShadowBanned ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {isShadowBanned ? 'Remove shadow ban' : 'Shadow ban'}
          </button>
          {/* Co-Host option - Only visible to actual Host (moderator), not breakout room hosts */}
          {isHostUser && !currentBreakoutRoom && role !== 'host' && (
            <button
              onClick={() => {
                onToggleCoHost?.(participantId, displayName, isCoHost);
                setShowMenu(false);
              }}
              className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center gap-2 ${
                isCoHost ? 'text-blue-400' : 'text-gray-300'
              }`}
            >
              {isCoHost ? <ShieldMinus className="w-3 h-3" /> : <ShieldPlus className="w-3 h-3" />}
              {isCoHost ? 'Remove Co-Host' : 'Make Co-Host'}
            </button>
          )}
          {/* Assign Category submenu */}
          {categories && categories.length > 0 && (
            <button
              ref={categoryBtnRef}
              onClick={() => {
                if (!showCategorySubmenu && categoryBtnRef.current) {
                  const rect = categoryBtnRef.current.getBoundingClientRect();
                  const submenuHeight = (categories.length + 1) * 28 + 10;
                  const submenuWidth = 160;
                  // Horizontal: prefer right side, fall back to left
                  const spaceRight = window.innerWidth - rect.right;
                  const left = spaceRight >= submenuWidth + 4 ? rect.right + 4 : rect.left - submenuWidth - 4;
                  // Vertical: prefer aligned to top of button, shift up if overflows bottom
                  let top = rect.top;
                  if (top + submenuHeight > window.innerHeight - 8) {
                    top = Math.max(8, window.innerHeight - submenuHeight - 8);
                  }
                  setSubmenuPosition({ top, left });
                }
                setShowCategorySubmenu(!showCategorySubmenu);
              }}
              className="w-full px-2.5 py-1.5 text-left text-[11px] text-gray-300 hover:bg-white/10 flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Crown className="w-3 h-3" /> Assign Category
              </span>
              <ChevronRight className="w-3 h-3 text-gray-500" />
            </button>
          )}
          <div className="border-t border-white/10 my-1" />
          <button
            onClick={() => { onKickParticipant?.(participantId, displayName); setShowMenu(false); }}
            className="w-full px-2.5 py-1.5 text-left text-[11px] text-red-400 hover:bg-white/10 flex items-center gap-2"
          >
            <UserMinus className="w-3 h-3" /> Remove from meeting
          </button>
        </div>,
        document.body
      )}

      {/* Category submenu - rendered as separate portal for proper positioning */}
      {showMenu && showCategorySubmenu && categories && categories.length > 0 && createPortal(
        <div
          ref={categorySubmenuRef}
          className="fixed bg-[#1a2744] rounded-lg shadow-2xl border border-white/10 py-1 min-w-[160px]"
          style={{ top: submenuPosition.top, left: submenuPosition.left, zIndex: 100000 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              onAssignCategory?.(participantId, null);
              setShowCategorySubmenu(false);
              setShowMenu(false);
            }}
            className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center justify-between ${
              !participantCategory ? 'text-white' : 'text-gray-400'
            }`}
          >
            <span>No Category</span>
            {!participantCategory && <Check className="w-3 h-3 text-green-400" />}
          </button>
          {categories.map(cat => {
            const CatIcon = CATEGORY_ICONS[cat.icon] || Crown;
            const isActive = participantCategory?.id === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  onAssignCategory?.(participantId, cat);
                  setShowCategorySubmenu(false);
                  setShowMenu(false);
                }}
                className={`w-full px-2.5 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center justify-between ${
                  isActive ? 'text-white' : 'text-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <CatIcon className="w-3 h-3" style={{ color: cat.color }} />
                  {cat.name}
                </span>
                {isActive && <Check className="w-3 h-3 text-green-400" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {/* Volume submenu - rendered as separate portal */}
      {showMenu && showVolumeSubmenu && createPortal(
        <div
          ref={volumeSubmenuRef}
          className="fixed bg-[#1a2744] rounded-lg shadow-2xl border border-white/10 p-3 w-[180px]"
          style={{ top: volumeSubmenuPosition.top, left: volumeSubmenuPosition.left, zIndex: 100000 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="text-[11px] text-gray-300">Volume</span>
            <span className="text-[11px] text-white ml-auto">{volume}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={volume}
            onChange={(e) => handleVolumeChange(Number(e.target.value))}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-green-500"
            style={{
              background: `linear-gradient(to right, #00c853 0%, #00c853 ${volume}%, #4b5563 ${volume}%, #4b5563 100%)`
            }}
          />
          <div className="flex justify-between mt-1">
            <span className="text-[9px] text-gray-500">0</span>
            <span className="text-[9px] text-gray-500">100</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Component to filter a single participant and report its status
function FilterableParticipant({ participantId, isLocal, localParticipant, hasRaisedHand, onLowerHand, onRequestUnmute, onMakePresenter, onToggleShadowBan, isShadowBanned, isCoHost, onToggleCoHost, isHostUser, localUserRole, filters, searchTerm, onStatusReport, onDisableWebcam, onMuteMic, onKickParticipant, isPresenter, onSpotlight, isSpotlighted, onToggleLocalMic, onToggleLocalCamera, categories, participantCategory, onAssignCategory, roleCategory, networkQuality, canSpotlight }) {
  const { displayName, webcamOn, micOn, metaData } = useParticipant(participantId);
  const { wasDemotedCoHost } = useSiteContext();

  // Get participant's role from metadata (for remote participants)
  const participantRole = metaData?.meetingRole;

  // Effective co-host: true if in coHosts Set OR metadata says co-host (unless explicitly demoted)
  const effectiveIsCoHost = isCoHost || (participantRole === 'co-host' && !wasDemotedCoHost(participantId));

  const name = isLocal ? (localParticipant?.displayName || 'You') : (displayName || 'Participant');

  // Report status to parent for aggregate calculations
  React.useEffect(() => {
    onStatusReport?.(participantId, { micOn, webcamOn, displayName: name });
  }, [participantId, micOn, webcamOn, name, onStatusReport]);

  // Check if participant matches filters
  const matchesFilters = useMemo(() => {
    // Check search term
    if (searchTerm && !name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // If no filters active, show all
    const hasActiveFilter = Object.values(filters).some(Boolean);
    if (!hasActiveFilter) return true;

    // Split filters into groups — within each group use OR, between groups use AND
    // Media/status group
    const mediaFilters = [filters.micOn, filters.micOff, filters.cameraOn, filters.cameraOff, filters.raisedHand];
    const hasMediaFilter = mediaFilters.some(Boolean);
    let matchesMedia = !hasMediaFilter;
    if (hasMediaFilter) {
      if (filters.micOn && micOn) matchesMedia = true;
      if (filters.micOff && !micOn) matchesMedia = true;
      if (filters.cameraOn && webcamOn) matchesMedia = true;
      if (filters.cameraOff && !webcamOn) matchesMedia = true;
      if (filters.raisedHand && hasRaisedHand) matchesMedia = true;
    }

    // Role group
    const hasRoleFilter = filters.hostsOnly || filters.presentersOnly || filters.attendeesOnly;
    let matchesRole = !hasRoleFilter;
    if (hasRoleFilter) {
      if (filters.hostsOnly && (roleCategory === 'host' || roleCategory === 'co-host')) matchesRole = true;
      if (filters.presentersOnly && roleCategory === 'presenter') matchesRole = true;
      if (filters.attendeesOnly && roleCategory === 'attendee') matchesRole = true;
    }

    // Category group
    const catFilterKeys = Object.keys(filters).filter(k => k.startsWith('cat_') && filters[k]);
    let matchesCat = catFilterKeys.length === 0;
    if (catFilterKeys.length > 0 && participantCategory) {
      matchesCat = catFilterKeys.some(k => k === `cat_${participantCategory.id}`);
    }

    return matchesMedia && matchesRole && matchesCat;
  }, [searchTerm, name, filters, micOn, webcamOn, hasRaisedHand, roleCategory, participantCategory]);

  if (!matchesFilters) return null;

  return (
    <ParticipantItem
      participantId={participantId}
      isLocal={isLocal}
      localParticipant={localParticipant}
      hasRaisedHand={hasRaisedHand}
      onLowerHand={onLowerHand}
      onRequestUnmute={onRequestUnmute}
      onMakePresenter={onMakePresenter}
      onToggleShadowBan={onToggleShadowBan}
      isShadowBanned={isShadowBanned}
      isCoHost={effectiveIsCoHost}
      onToggleCoHost={onToggleCoHost}
      isHostUser={isHostUser}
      localUserRole={localUserRole}
      participantRole={participantRole}
      onDisableWebcam={onDisableWebcam}
      onMuteMic={onMuteMic}
      onKickParticipant={onKickParticipant}
      isPresenter={isPresenter}
      onSpotlight={onSpotlight}
      isSpotlighted={isSpotlighted}
      onToggleLocalMic={onToggleLocalMic}
      onToggleLocalCamera={onToggleLocalCamera}
      categories={categories}
      participantCategory={participantCategory}
      onAssignCategory={onAssignCategory}
      networkQuality={networkQuality}
      canSpotlight={canSpotlight}
    />
  );
}

// Raised hand item for the yellow raised hands section
function RaisedHandItem({ participantId, isLocal, localParticipant, index, timeSince, isHostUser, onLowerHand, onSpotlight, isSpotlighted, onMakePresenter, canSpotlight }) {
  const { displayName } = useParticipant(participantId);
  const name = isLocal ? (localParticipant?.displayName || 'You') : (displayName || 'Participant');
  const color = getColorFromName(name);
  const initials = getInitials(name);

  return (
    <div className="flex items-center justify-between p-1.5 bg-yellow-500/10 rounded-lg">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[10px] text-yellow-500 font-medium w-3">{index + 1}.</span>
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[8px] font-bold"
          style={{ backgroundColor: color }}
        >
          {initials}
        </div>
        <span className="text-[11px] text-white font-medium truncate">{name}</span>
        {timeSince && (
          <span className="text-[9px] text-gray-500 flex items-center gap-0.5 flex-shrink-0">
            <Clock className="w-2.5 h-2.5" />
            {timeSince}
          </span>
        )}
      </div>
      {(canSpotlight || isHostUser) && (
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {canSpotlight && (
            <button
              onClick={() => onSpotlight?.(participantId, name, isSpotlighted)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Spotlight"
            >
              <Star className="w-3 h-3 text-gray-400 hover:text-yellow-400" />
            </button>
          )}
          {isHostUser && (
            <>
              <button
                onClick={() => onMakePresenter?.(participantId, name)}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                title="Promote"
              >
                <ArrowUp className="w-3 h-3 text-gray-400 hover:text-blue-400" />
              </button>
              <button
                onClick={() => onLowerHand?.(participantId)}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                title="Lower hand"
              >
                <X className="w-3 h-3 text-gray-400 hover:text-red-400" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Waiting Room Settings Modal — reads/writes dynamic room data
function WaitingRoomSettingsModal({ meetingSettings, setMeetingSettings, saving, setSaving, onClose, onWaitingRoomChange, onAutoAdmitChange }) {
  const isWaitingRoomEnabled = meetingSettings?.waitingRoom ?? meetingSettings?.hostApprovalRequired ?? false;
  const autoAdmitRegistered = meetingSettings?.autoAdmitRegistered ?? false;
  const welcomeMessage = meetingSettings?.waitingRoomMessage ?? '';
  const roomId = meetingSettings?.id || meetingSettings?._id;

  const handleToggleWaitingRoom = async () => {
    const newValue = !isWaitingRoomEnabled;
    onWaitingRoomChange(newValue);
  };

  const handleToggleAutoAdmit = async () => {
    const newValue = !autoAdmitRegistered;
    onAutoAdmitChange(newValue);
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    if (value.length <= 200) {
      setMeetingSettings(prev => ({ ...prev, waitingRoomMessage: value }));
    }
  };

  const handleMessageBlur = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('waitingRoomMessage', welcomeMessage.trim());
      await updateMeetingRoom(roomId, formData);
    } catch (e) {
      toast.error('Failed to update welcome message');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[28rem] bg-[#001233] border border-white/10 rounded-lg shadow-2xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-bold text-white">Waiting Room Settings</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Enable Waiting Room */}
        <div className="flex items-center justify-between py-4 border-b border-white/10">
          <div>
            <p className="text-sm font-semibold text-white">Enable Waiting Room</p>
            <p className="text-xs text-gray-400 mt-0.5">New participants must be admitted</p>
          </div>
          <button
            onClick={handleToggleWaitingRoom}
            disabled={saving}
            className={`relative w-11 h-6 rounded-full transition-colors ${isWaitingRoomEnabled ? 'bg-[#6a1fbf]' : 'bg-white/20'} ${saving ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${isWaitingRoomEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Auto-admit registered users */}
        <div className="flex items-center justify-between py-4 border-b border-white/10">
          <div>
            <p className="text-sm font-semibold text-white">Auto-admit registered users</p>
            <p className="text-xs text-gray-400 mt-0.5">Skip waiting room for registered attendees</p>
          </div>
          <button
            onClick={handleToggleAutoAdmit}
            disabled={saving}
            className={`relative w-11 h-6 rounded-full transition-colors ${autoAdmitRegistered ? 'bg-[#6a1fbf]' : 'bg-white/20'} ${saving ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${autoAdmitRegistered ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Custom Welcome Message */}
        <div className="pt-4">
          <p className="text-sm font-semibold text-white mb-2">Custom Welcome Message</p>
          <textarea
            value={welcomeMessage}
            onChange={handleMessageChange}
            onBlur={handleMessageBlur}
            placeholder="e.g., Please wait while the host reviews your request to join"
            maxLength={200}
            className="w-full h-24 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#6a1fbf]/50 resize-y"
          />
          <p className="text-xs text-gray-500 mt-1">{welcomeMessage.length}/200 characters</p>
        </div>

        {saving && (
          <p className="text-xs text-blue-400 mt-3 text-center">Saving...</p>
        )}
      </div>
    </div>
  );
}

// Waiting room section for the participants panel
function WaitingRoomSection({ waitingParticipants, onAdmit, onAdmitAll, onDeny, isExpanded, onToggle, onSendMessageToWaiting, waitingRoomChatMessages = {}, autoAdmitRegisteredInWaitingRoom }) {
  const { meetingSettings, setMeetingSettings, isAuthenticated, meetingSession } = useSiteContext();
  const { publish: publishMeetingSettings } = usePubSub("MEETING_SETTINGS");
  // Per-browser chime mute flag. WaitingRoomProvider was never mounted, so
  // we persist this directly to localStorage — MeetingContainerNew's
  // playWaitingChime reads the same key.
  const [chimeMuted, setChimeMutedState] = useState(() => {
    try { return localStorage.getItem('rlink_waiting_room_chime_muted') === '1'; } catch { return false; }
  });
  const setChimeMuted = (muted) => {
    const next = !!muted;
    setChimeMutedState(next);
    try { localStorage.setItem('rlink_waiting_room_chime_muted', next ? '1' : '0'); } catch (_) {}
  };
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [showWaitingRoomSettings, setShowWaitingRoomSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Waiting room toggle handler — same logic as bottom bar Settings > General
  const handleWaitingRoomChange = async (newValue) => {
    if (!newValue && onAdmitAll) onAdmitAll();
    const updates = { waitingRoom: newValue, hostApprovalRequired: newValue };
    if (!newValue) updates.autoAdmitRegistered = false;
    setMeetingSettings(prev => ({ ...prev, ...updates }));
    publishMeetingSettings({ data: { ...meetingSettings, ...updates } });
    setSavingSettings(true);
    try {
      if (isAuthenticated) {
        meetingServices.updateMeetingSessionSettings({
          meetingId: meetingSettings?.roomId,
          sessionId: meetingSession,
          guestCanMute: meetingSettings?.guestCanUnmute,
          waitingRoomEnabled: newValue,
        });
      }
      const fd = new FormData();
      fd.append('waitingRoom', newValue);
      if (!newValue) fd.append('autoAdmitRegistered', false);
      await updateMeetingRoom(meetingSettings?.id || meetingSettings?._id, fd);
    } catch (e) {
      toast.error('Failed to update waiting room setting');
      setMeetingSettings(prev => ({ ...prev, waitingRoom: !newValue, hostApprovalRequired: !newValue }));
    } finally {
      setSavingSettings(false);
    }
  };

  // Auto-admit toggle handler — same logic as bottom bar Settings > General
  const handleAutoAdmitChange = async (newValue) => {
    const isWREnabled = meetingSettings?.waitingRoom || meetingSettings?.hostApprovalRequired;
    const updates = { autoAdmitRegistered: newValue };
    if (newValue && !isWREnabled) { updates.waitingRoom = true; updates.hostApprovalRequired = true; }
    setMeetingSettings(prev => ({ ...prev, ...updates }));
    publishMeetingSettings({ data: { ...meetingSettings, ...updates } });
    // Auto-admit any registered users already in the waiting room
    if (newValue && autoAdmitRegisteredInWaitingRoom) {
      autoAdmitRegisteredInWaitingRoom();
    }
    setSavingSettings(true);
    try {
      const fd = new FormData();
      fd.append('autoAdmitRegistered', newValue);
      if (newValue && !isWREnabled) fd.append('waitingRoom', true);
      await updateMeetingRoom(meetingSettings?.id || meetingSettings?._id, fd);
    } catch (e) {
      toast.error('Failed to update auto-admit setting');
      setMeetingSettings(prev => ({ ...prev, autoAdmitRegistered: !newValue }));
    } finally {
      setSavingSettings(false);
    }
  };
  const [carryOverEnabled, setCarryOverEnabled] = useState(() => {
    // Restore toggle state from sessionStorage (survives tab switches)
    try {
      const saved = sessionStorage.getItem('waitingRoomCarryOverToggles');
      return saved ? JSON.parse(saved) : {};
    } catch (e) { return {}; }
  }); // { [odId]: boolean }
  const chatEndRef = useRef(null);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [waitingRoomChatMessages, messageTarget]);

  if (!waitingParticipants || waitingParticipants.length === 0) return null;

  // Format time since joined waiting room
  const formatWaitTime = (joinedAt) => {
    if (!joinedAt) return '';
    const seconds = Math.floor((Date.now() - new Date(joinedAt).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s waiting`;
    return `${Math.floor(seconds / 60)}m waiting`;
  };

  const handleSendMessage = () => {
    if (messageTarget && messageText.trim()) {
      onSendMessageToWaiting(messageTarget, messageText.trim());
      setMessageText('');
    }
  };

  const getUnreadCount = (odId) => {
    const msgs = waitingRoomChatMessages[odId] || [];
    return msgs.filter((m) => m.senderRole === 'participant').length;
  };

  // Save waiting room chat to sessionStorage for carry-over when admitting
  const handleAdmitWithCarryOver = (odId) => {
    if (carryOverEnabled[odId]) {
      const participant = waitingParticipants.find(p => p.odId === odId);
      const chatHistory = waitingRoomChatMessages[odId] || [];
      if (participant && chatHistory.length > 0) {
        try {
          const existing = JSON.parse(sessionStorage.getItem('waitingRoomChatCarryOver') || '{}');
          existing[participant.odName] = chatHistory;
          sessionStorage.setItem('waitingRoomChatCarryOver', JSON.stringify(existing));
        } catch (e) {
          console.warn('[WaitingRoom] Failed to save carry-over:', e);
        }
      }
    }
    onAdmit?.(odId);
  };

  return (
    <div className="mb-2 rounded-lg bg-blue-500/5 border border-blue-500/20">
      {/* Header must be a <div>, not a <button>. Nesting real <button>s
          inside an outer <button> is invalid HTML — browsers close the outer
          button early and the nested icon handlers (chime mute, admit-all,
          deny-all, settings) silently never fire. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.();
          }
        }}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-500/10 rounded-t-lg transition-colors cursor-pointer select-none"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-blue-400" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
          )}
          <Clock className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs font-medium text-blue-400">
            Waiting Room ({waitingParticipants.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          {waitingParticipants.length > 0 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAdmitAll?.(); }}
                className="p-1 hover:bg-green-500/20 rounded transition-colors"
                title="Admit all"
              >
                <UserPlus className="w-3.5 h-3.5 text-green-400" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  waitingParticipants.forEach(p => onDeny?.(p.odId));
                }}
                className="p-1 hover:bg-red-500/20 rounded transition-colors"
                title="Deny all"
              >
                <X className="w-3.5 h-3.5 text-red-400" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setChimeMuted(!chimeMuted); }}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title={chimeMuted ? 'Unmute waiting-room chime' : 'Mute waiting-room chime'}
          >
            {chimeMuted
              ? <VolumeX className="w-3.5 h-3.5 text-gray-400" />
              : <Volume2 className="w-3.5 h-3.5 text-blue-400" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowWaitingRoomSettings(true); }}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Waiting room settings"
          >
            <Settings className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-2 pb-2 space-y-1">
          {/* Bulk admit/deny buttons */}
          {waitingParticipants.length > 1 && (
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => onAdmitAll?.()}
                className="flex-1 h-8 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 rounded-md text-xs font-medium transition-colors flex items-center justify-center"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Admit All ({waitingParticipants.length})
              </button>
              <button
                onClick={() => {
                  waitingParticipants.forEach(p => onDeny?.(p.odId));
                }}
                className="flex-1 h-8 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-md text-xs font-medium transition-colors flex items-center justify-center"
              >
                <X className="w-3.5 h-3.5 mr-1.5" /> Deny All
              </button>
            </div>
          )}

          {waitingParticipants.map((participant) => {
            const name = participant.odName || 'Unknown';
            const initials = getInitials(name);

            return (
              <div
                key={participant.odId}
                className="flex items-center justify-between p-2 bg-blue-500/10 rounded-lg group"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    {participant.odAvatar ? (
                      <img src={participant.odAvatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-bold text-white">{initials}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-white truncate">{name}</p>
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      <Clock className="w-2.5 h-2.5" />
                      <span>{formatWaitTime(participant.joinedAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  <button
                    onClick={() => setMessageTarget(messageTarget === participant.odId ? null : participant.odId)}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors relative"
                    title="Chat"
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                    {getUnreadCount(participant.odId) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full text-[7px] text-white flex items-center justify-center font-bold">
                        {getUnreadCount(participant.odId) > 9 ? '!' : getUnreadCount(participant.odId)}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => handleAdmitWithCarryOver(participant.odId)}
                    className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded transition-colors"
                    title="Admit"
                  >
                    <Check className="w-3.5 h-3.5 text-green-400" />
                  </button>
                  <button
                    onClick={() => onDeny?.(participant.odId)}
                    className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded transition-colors"
                    title="Deny"
                  >
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline Chat Panel */}
      {messageTarget && isExpanded && (
        <div className="mx-2 mb-2 bg-white/5 border border-white/10 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/10">
            <div className="flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3 text-blue-400" />
              <span className="text-[11px] font-medium text-white truncate">
                {waitingParticipants.find(p => p.odId === messageTarget)?.odName || 'Participant'}
              </span>
            </div>
            <button
              onClick={() => { setMessageTarget(null); setMessageText(''); }}
              className="p-0.5 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-3 h-3 text-gray-400" />
            </button>
          </div>
          {/* Messages */}
          <div className="max-h-40 overflow-y-auto px-2 py-1.5 space-y-1.5">
            {(waitingRoomChatMessages[messageTarget] || []).length === 0 && (
              <p className="text-white/30 text-[10px] text-center py-2">No messages yet</p>
            )}
            {(waitingRoomChatMessages[messageTarget] || []).map((msg) => {
              const isHost = msg.senderRole === 'host';
              return (
                <div key={msg.id} className={`flex ${isHost ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-2 py-1 ${
                    isHost ? 'bg-blue-500/30 text-white' : 'bg-white/10 text-white'
                  }`}>
                    {!isHost && (
                      <p className="text-[9px] font-medium text-purple-300">{msg.senderName}</p>
                    )}
                    <p className="text-[11px] break-words">{msg.message}</p>
                    <p className={`text-[8px] ${isHost ? 'text-white/50' : 'text-white/30'}`}>
                      {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>
          {/* Show in meeting chat toggle */}
          <div className="px-2 py-1 border-t border-white/5">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={!!carryOverEnabled[messageTarget]}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    const updated = { ...carryOverEnabled, [messageTarget]: enabled };
                    setCarryOverEnabled(updated);
                    // Persist toggle state so it survives tab switches
                    try {
                      sessionStorage.setItem('waitingRoomCarryOverToggles', JSON.stringify(updated));
                    } catch (err) { /* ignore */ }
                  }}
                  className="sr-only"
                />
                <div className={`w-6 h-3 rounded-full transition-colors ${carryOverEnabled[messageTarget] ? 'bg-blue-500' : 'bg-white/20'}`}>
                  <div className={`absolute top-0.5 w-2 h-2 bg-white rounded-full transition-transform ${carryOverEnabled[messageTarget] ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                </div>
              </div>
              <span className="text-white/40 text-[10px]">Show in meeting chat</span>
            </label>
          </div>
          {/* Input */}
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-t border-white/10">
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              placeholder="Reply..."
              className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              autoFocus
            />
            <button
              onClick={handleSendMessage}
              disabled={!messageText.trim()}
              className="p-1 bg-blue-500 hover:bg-blue-600 rounded text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Send className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Waiting Room Settings Modal */}
      {showWaitingRoomSettings && createPortal(
        <WaitingRoomSettingsModal
          meetingSettings={meetingSettings}
          setMeetingSettings={setMeetingSettings}
          saving={savingSettings}
          setSaving={setSavingSettings}
          onClose={() => setShowWaitingRoomSettings(false)}
          onWaitingRoomChange={handleWaitingRoomChange}
          onAutoAdmitChange={handleAutoAdmitChange}
        />,
        document.body
      )}
    </div>
  );
}

// Broadcast Modal — Host sends announcement to all participants via Socket.IO
const MAX_BROADCAST_CHARS = 280;

const BROADCAST_TARGETS = [
  { value: 'everyone', label: 'Everyone', icon: 'users', description: 'All participants including waiting room' },
  { value: 'panelists', label: 'Panelists Only', icon: 'mic', description: 'Hosts, co-hosts, and presenters on stage' },
];

function BroadcastModal({ isOpen, onClose, onSend, isSending }) {
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState('everyone');
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  const remainingChars = MAX_BROADCAST_CHARS - message.length;
  const isValid = message.trim().length > 0 && remainingChars >= 0;

  const selectedTarget = BROADCAST_TARGETS.find(t => t.value === target);

  const handleSend = () => {
    if (!isValid) return;
    onSend({ message: message.trim(), target });
    setMessage('');
    setTarget('everyone');
  };

  const handleClose = () => {
    setMessage('');
    setTarget('everyone');
    setShowTargetDropdown(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#001233] border border-white/10 rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Host Broadcast</h2>
              <p className="text-xs text-gray-400">Send an announcement to participants</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Message Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_BROADCAST_CHARS))}
              placeholder="Type your announcement..."
              className="w-full min-h-[120px] bg-white/5 border border-white/10 text-white placeholder:text-gray-500 resize-none rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              autoFocus
            />
            <div className="flex justify-end">
              <span className={`text-xs ${remainingChars < 20 ? 'text-orange-400' : 'text-gray-500'}`}>
                {remainingChars} characters remaining
              </span>
            </div>
          </div>

          {/* Target Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Send to</label>
            <div className="relative">
              <button
                onClick={() => setShowTargetDropdown(!showTargetDropdown)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {selectedTarget?.icon === 'mic' ? <Mic className="w-4 h-4 text-purple-400" /> : <Users className="w-4 h-4 text-purple-400" />}
                  <span>{selectedTarget?.label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTargetDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showTargetDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0a1929] border border-white/10 rounded-lg shadow-xl z-10 overflow-hidden">
                  {BROADCAST_TARGETS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setTarget(opt.value);
                        setShowTargetDropdown(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-white/10 transition-colors ${
                        target === opt.value ? 'bg-white/5 text-white' : 'text-gray-300'
                      }`}
                    >
                      {opt.icon === 'mic' ? <Mic className="w-4 h-4 text-purple-400" /> : <Users className="w-4 h-4 text-purple-400" />}
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">{selectedTarget?.description}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10 bg-white/5">
          <button
            onClick={handleClose}
            disabled={isSending}
            className="px-4 py-2 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!isValid || isSending}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Broadcast
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Roles & Categories Configuration ──

const PERMISSION_GROUPS = [
  {
    title: 'Moderation',
    permissions: [
      { key: 'canMuteOthers', label: 'Mute others' },
      { key: 'canUnmuteOthers', label: 'Unmute others' },
      { key: 'canDisableCameras', label: 'Disable cameras' },
      { key: 'canRemoveParticipants', label: 'Remove participants' },
      { key: 'canManageWaitingRoom', label: 'Manage waiting room' },
    ]
  },
  {
    title: 'Management',
    permissions: [
      { key: 'canPromote', label: 'Promote/demote roles' },
      { key: 'canAssignRoles', label: 'Assign custom roles' },
      { key: 'canManageBreakout', label: 'Manage breakout rooms' },
      { key: 'canBroadcast', label: 'Send broadcasts' },
      { key: 'canRecord', label: 'Start/stop recording' },
    ]
  },
  {
    title: 'Participation',
    permissions: [
      { key: 'canSpeak', label: 'Unmute self' },
      { key: 'canShareVideo', label: 'Share video' },
      { key: 'canScreenShare', label: 'Share screen' },
      { key: 'canChat', label: 'Send messages' },
      { key: 'canReact', label: 'Send reactions' },
    ]
  },
];

const CATEGORY_ICONS = { Crown, Users, Mic, Shield };

function useParticipantRoles() {
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Fetch roles and categories from API on mount
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [rolesRes, catsRes] = await Promise.all([
          requests.get('/meeting-config/roles'),
          requests.get('/meeting-config/categories'),
        ]);
        if (cancelled) return;
        if (rolesRes?.success) setRoles(rolesRes.data);
        if (catsRes?.success) setCategories(catsRes.data);
      } catch (err) {
        console.error('[useParticipantRoles] Failed to fetch:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const addRole = async (role) => {
    try {
      const res = await requests.post('/meeting-config/roles', role);
      if (res?.success) {
        setRoles(prev => [...prev, res.data]);
      }
    } catch (err) {
      console.error('[useParticipantRoles] Failed to create role:', err);
      toast.error('Failed to create role');
    }
  };

  const updateRole = async (id, updates) => {
    try {
      const res = await requests.put(`/meeting-config/roles/${id}`, updates);
      if (res?.success) {
        setRoles(prev => prev.map(r => r.id === id ? res.data : r));
      }
    } catch (err) {
      console.error('[useParticipantRoles] Failed to update role:', err);
      toast.error('Failed to update role');
    }
  };

  const deleteRole = async (id) => {
    try {
      const res = await requests.delete(`/meeting-config/roles/${id}`);
      if (res?.success) {
        setRoles(prev => prev.filter(r => r.id !== id));
      }
    } catch (err) {
      console.error('[useParticipantRoles] Failed to delete role:', err);
      toast.error('Failed to delete role');
    }
  };

  const addCategory = async (category) => {
    try {
      const res = await requests.post('/meeting-config/categories', category);
      if (res?.success) {
        setCategories(prev => [...prev, res.data]);
      }
    } catch (err) {
      console.error('[useParticipantRoles] Failed to create category:', err);
      toast.error('Failed to create category');
    }
  };

  const deleteCategory = async (id) => {
    try {
      const res = await requests.delete(`/meeting-config/categories/${id}`);
      if (res?.success) {
        setCategories(prev => prev.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error('[useParticipantRoles] Failed to delete category:', err);
      toast.error('Failed to delete category');
    }
  };

  return { roles, categories, loaded, addRole, updateRole, deleteRole, addCategory, deleteCategory };
}

// ── Role Card (expandable permissions) ──

function RoleCard({ role, onDelete, expanded, onToggle }) {

  return (
    <div className="rounded-lg bg-white/5 border border-white/10 overflow-hidden">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
          <span className="font-medium text-white text-sm">{role.name}</span>
          {role.isSystem && (
            <span className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded text-gray-400">System</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggle}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
          >
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </button>
          {!role.isSystem && (
            <button onClick={onDelete} className="p-1.5 hover:bg-red-500/20 rounded transition-colors">
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-white/10">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {Object.entries(role.permissions).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                {value ? <Check className="w-3 h-3 text-green-400" /> : <X className="w-3 h-3 text-gray-500" />}
                <span className={value ? 'text-gray-300' : 'text-gray-500'}>
                  {key.replace('can', '').replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Category Card ──

function CategoryCard({ category, onDelete }) {
  const Icon = CATEGORY_ICONS[category.icon] || Crown;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${category.color}20` }}>
          <Icon className="w-4 h-4" style={{ color: category.color }} />
        </div>
        <span className="font-medium text-white text-sm">{category.name}</span>
      </div>
      <button onClick={onDelete} className="p-1.5 hover:bg-red-500/20 rounded transition-colors">
        <Trash2 className="w-4 h-4 text-red-400" />
      </button>
    </div>
  );
}

// ── Roles & Categories Manager Modal ──

function RolesAndCategoriesModal({ isOpen, onClose, roles, categories, onAddRole, onDeleteRole, onAddCategory, onDeleteCategory }) {
  const [activeTab, setActiveTab] = useState('roles');
  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const [showNewRole, setShowNewRole] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleColor, setNewRoleColor] = useState('#6a1fbf');
  const [newRolePermissions, setNewRolePermissions] = useState({});
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#f59e0b');
  const [newCategoryIcon, setNewCategoryIcon] = useState('Crown');

  const handleCreateRole = () => {
    if (!newRoleName.trim()) return;
    onAddRole({ name: newRoleName, color: newRoleColor, permissions: newRolePermissions });
    setNewRoleName('');
    setNewRoleColor('#6a1fbf');
    setNewRolePermissions({});
    setShowNewRole(false);
  };

  const handleCreateCategory = () => {
    if (!newCategoryName.trim()) return;
    onAddCategory({ name: newCategoryName, color: newCategoryColor, icon: newCategoryIcon });
    setNewCategoryName('');
    setNewCategoryColor('#f59e0b');
    setNewCategoryIcon('Crown');
    setShowNewCategory(false);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[80vh] bg-[#001233] border border-white/10 rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">Roles & Categories</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('roles')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'roles' ? 'text-white border-b-2 border-purple-500' : 'text-gray-400 hover:text-white'}`}
          >
            Roles & Permissions
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'categories' ? 'text-white border-b-2 border-purple-500' : 'text-gray-400 hover:text-white'}`}
          >
            Categories
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'roles' && (
            <div className="space-y-3">
              {roles.map(role => (
                <RoleCard key={role.id} role={role} onDelete={() => onDeleteRole(role.id)} expanded={expandedRoleId === role.id} onToggle={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)} />
              ))}

              {showNewRole ? (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                  <div className="flex gap-3">
                    <input
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      placeholder="Role name"
                      className="flex-1 h-9 px-3 text-sm bg-white/5 border border-white/10 rounded-md text-white placeholder:text-gray-500 focus:outline-none focus:border-[#6a1fbf]/50"
                    />
                    <input
                      type="color"
                      value={newRoleColor}
                      onChange={(e) => setNewRoleColor(e.target.value)}
                      className="w-10 h-9 rounded cursor-pointer border border-white/10"
                    />
                  </div>
                  <div className="space-y-3">
                    {PERMISSION_GROUPS.map(group => (
                      <div key={group.title}>
                        <p className="text-xs text-gray-400 mb-2">{group.title}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {group.permissions.map(perm => (
                            <label key={perm.key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newRolePermissions[perm.key] || false}
                                onChange={(e) => setNewRolePermissions(prev => ({ ...prev, [perm.key]: e.target.checked }))}
                                className="rounded accent-[#6a1fbf]"
                              />
                              {perm.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowNewRole(false)} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">Cancel</button>
                    <button onClick={handleCreateRole} className="px-3 py-1.5 text-sm bg-[#6a1fbf] text-white rounded-lg hover:bg-[#5a18a5] transition-colors">Create Role</button>
                  </div>
                </div>
              ) : (
                null
                /* Hidden: Add Custom Role button
                <button
                  onClick={() => setShowNewRole(true)}
                  className="w-full p-3 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Custom Role
                </button>
                */
              )}
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400 mb-4">
                Categories help you organize participants (e.g., VIPs, team members). They don't affect permissions.
              </p>
              {categories.map(category => (
                <CategoryCard key={category.id} category={category} onDelete={() => onDeleteCategory(category.id)} />
              ))}

              {showNewCategory ? (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                  <div className="flex gap-3">
                    <input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Category name"
                      className="flex-1 h-9 px-3 text-sm bg-white/5 border border-white/10 rounded-md text-white placeholder:text-gray-500 focus:outline-none focus:border-[#6a1fbf]/50"
                    />
                    <input
                      type="color"
                      value={newCategoryColor}
                      onChange={(e) => setNewCategoryColor(e.target.value)}
                      className="w-10 h-9 rounded cursor-pointer border border-white/10"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Icon</p>
                    <div className="flex gap-2">
                      {Object.entries(CATEGORY_ICONS).map(([key, Icon]) => (
                        <button
                          key={key}
                          onClick={() => setNewCategoryIcon(key)}
                          className={`p-2 rounded-lg transition-colors ${newCategoryIcon === key ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                        >
                          <Icon className="w-4 h-4" />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowNewCategory(false)} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">Cancel</button>
                    <button onClick={handleCreateCategory} className="px-3 py-1.5 text-sm bg-[#6a1fbf] text-white rounded-lg hover:bg-[#5a18a5] transition-colors">Create Category</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewCategory(true)}
                  className="w-full p-3 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Category
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Filter Checkbox Item (matches shadcn DropdownMenuCheckboxItem style) ──
function FilterCheckItem({ label, icon, checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`w-full pl-2 pr-3 py-1.5 text-left text-[11px] hover:bg-white/10 flex items-center gap-2 transition-colors ${
        checked ? 'text-white' : 'text-gray-300'
      }`}
    >
      <span className="w-4 flex-shrink-0 flex items-center justify-center">
        {checked && <Check className="w-3.5 h-3.5 text-white" />}
      </span>
      {icon}
      <span>{label}</span>
    </button>
  );
}

export default function ParticipantsPanel({ onMuteAll, onUnmuteAll, onLowerAllHands, onLowerHand, waitingParticipants = [], onAdmitParticipant, onAdmitAllParticipants, onDenyParticipant, onSendMessageToWaiting, waitingRoomChatMessages = {}, autoAdmitRegisteredInWaitingRoom }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState(['waitingRoom', 'raisedHands', 'hosts', 'presenters', 'attendees']);
  const [participantStatuses, setParticipantStatuses] = useState({});
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showRolesManager, setShowRolesManager] = useState(false);
  const { roles, categories, addRole, deleteRole, addCategory, deleteCategory } = useParticipantRoles();

  const [filters, setFilters] = useState({
    micOn: false,
    micOff: false,
    cameraOn: false,
    cameraOff: false,
    raisedHand: false,
    hostsOnly: false,
    presentersOnly: false,
    attendeesOnly: false,
  });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState({ top: 0, left: 0 });
  const filterBtnRef = useRef(null);
  const filterDropdownRef = useRef(null);

  const mMeeting = useMeeting();
  const participants = mMeeting?.participants;
  const localParticipant = mMeeting?.localParticipant;
  const networkQualities = mMeeting?.networkQualities;

  // Get shadow banned participants and presenters from context
  const { shadowBannedParticipants, setShadowBannedParticipants, presenters, setPresenters } = useMeetingAppContext();

  // Get co-host state and meeting role from SiteContext
  const { meetingRole, isCoHost, wasDemotedCoHost, addCoHost, removeCoHost, meetingSettings, user } = useSiteContext();
  // Check for host, moderator, or co-host role values (includes dynamically promoted co-hosts)
  // Breakout room hosts also get host-level permissions (mute/kick) within their breakout room
  const { isBreakoutModerator, currentBreakoutRoom: localBreakoutRoom } = useBreakoutRooms();
  const isHostUser = meetingRole === 'moderator' || meetingRole === 'host' || meetingRole === 'co-host' || isCoHost(localParticipant?.id) || (isBreakoutModerator && !!localBreakoutRoom);
  const localIsPresenter = (() => {
    if (meetingRole === 'presenter' || meetingRole === 'speaker') return true;
    const localId = localParticipant?.id;
    if (!localId || !presenters) return false;
    const numericUid = typeof localId === 'number' ? localId : stringToNumericUid(localId);
    return presenters.has(localId) || presenters.has(String(localId)) || presenters.has(numericUid);
  })();
  const canSpotlight = isHostUser || localIsPresenter;

  // Socket.IO-based mute for reliability (replaces PubSub-only muting)
  const meetingState = useMeetingState();

  // Category assignments shared across all participants via Socket.IO
  const categoryAssignments = meetingState?.categoryAssignments || {};
  const handleAssignCategory = React.useCallback((participantId, category) => {
    if (meetingState?.emitCategoryAssign) {
      meetingState.emitCategoryAssign(participantId, category);
    }
    toast.success(category ? `Assigned "${category.name}" category` : 'Category removed');
  }, [meetingState]);

  // Get presence snapshot from RTM for reliable mute state verification
  const rtm = useAgoraRTM();

  // PubSub for requesting mic/video enable and mute all
  const { publish: publishMuteMic } = usePubSub('MUTE_MIC');
  const { publish: publishMicPermissionRequest } = usePubSub('MIC_PERMISSION_REQUEST');
  const { publish: publishVideoPermissionRequest } = usePubSub('VIDEO_PERMISSION_REQUEST');
  const { publish: publishDisableWebcam } = usePubSub('DISABLE_WEBCAM');
  const { publish: publishSpotlight } = usePubSub('SPOTLIGHT_PARTICIPANT');
  const { publish: publishMakePresenter } = usePubSub('MAKE_PRESENTER');
  const { publish: publishShadowBan } = usePubSub('SHADOW_BAN');
  const { publish: publishRoleChange } = usePubSub('ROLE_CHANGE');
  const { publish: publishKickParticipant } = usePubSub('KICK_PARTICIPANT');

  // Calculate if all remote participants are muted based on actual status + presence snapshot
  const allRemoteMuted = useMemo(() => {
    const remoteStatuses = Object.entries(participantStatuses)
      .filter(([id]) => id !== localParticipant?.id);
    if (remoteStatuses.length === 0) return false;
    return remoteStatuses.every(([id, status]) => {
      if (!status.micOn) return true;
      // Also check presence state for a more reliable indicator
      const presenceState = rtm?.presenceSnapshot?.get?.(String(id))?.states;
      if (presenceState?.micMuted === "true") return true;
      return false;
    });
  }, [participantStatuses, localParticipant?.id, rtm?.presenceSnapshot]);

  // Handle status reports from child components
  const handleStatusReport = React.useCallback((participantId, status) => {
    setParticipantStatuses(prev => {
      if (prev[participantId]?.micOn === status.micOn && prev[participantId]?.webcamOn === status.webcamOn) {
        return prev; // No change
      }
      return { ...prev, [participantId]: status };
    });
  }, []);

  // Handle mute all - only mutes, doesn't toggle
  // Uses Socket.IO broadcast for reliability (single message to all participants)
  const handleMuteAll = () => {
    if (onMuteAll) {
      onMuteAll();
    } else {
      // Fallback: use Socket.IO directly
      meetingState.emitForceMuteAll();
    }
  };

  // Handle request unmute all - sends request to all participants
  // DISABLED: unmute-all functionality hidden — button is mute-only
  // const handleRequestUnmuteAll = () => {
  //   try {
  //     if (onUnmuteAll) {
  //       onUnmuteAll();
  //     } else {
  //       // Send mic permission request to all remote participants
  //       participants?.forEach((participant) => {
  //         try {
  //           if (participant?.id && participant.id !== localParticipant?.id && !participant.micOn) {
  //             publishMicPermissionRequest(
  //               { targetParticipantId: participant.id, granted: true },
  //               { persist: false }
  //             );
  //           }
  //         } catch (err) {
  //           console.error('[handleRequestUnmuteAll] Error for participant:', participant?.id, err);
  //         }
  //       });
  //     }
  //   } catch (error) {
  //     console.error('[handleRequestUnmuteAll] Error:', error);
  //   }
  // };

  // Handle individual unmute/camera request
  const handleRequestUnmute = (participantId, type) => {
    try {
      if (type === 'mic') {
        publishMicPermissionRequest(
          { targetParticipantId: participantId, granted: true },
          { persist: false }
        );
      } else if (type === 'camera') {
        publishVideoPermissionRequest(
          { targetParticipantId: participantId, granted: true },
          { persist: false }
        );
      }
    } catch (error) {
      console.error('[handleRequestUnmute] Error:', error);
    }
  };

  // Handle make presenter - grants screen share permission to attendee
  // This is separate from spotlight which only highlights video
  const handleMakePresenter = (participantId, displayName) => {
    // Check if participant is already a presenter using the presenters state
    // Use numeric UID conversion for robust comparison (Agora uses numeric UIDs)
    const numericUid = typeof participantId === 'number' ? participantId : stringToNumericUid(participantId);
    const isCurrentlyPresenter = presenters?.has(participantId) ||
                                  presenters?.has(String(participantId)) ||
                                  presenters?.has(numericUid);
    const action = isCurrentlyPresenter ? 'remove' : 'add';

    // Update local state immediately for instant UI feedback
    // Store all ID formats for comparison flexibility
    setPresenters(prev => {
      const newSet = new Set(prev);
      if (action === 'add') {
        newSet.add(participantId);
        newSet.add(String(participantId));
        newSet.add(numericUid);
      } else {
        newSet.delete(participantId);
        newSet.delete(String(participantId));
        newSet.delete(numericUid);
      }
      return newSet;
    });

    // Publish to sync with other participants
    publishMakePresenter(
      JSON.stringify({
        participantId,
        displayName: displayName || 'Participant',
        action
      }),
      { persist: true }
    );

    console.log(`Make presenter: ${action} for ${displayName} (${participantId})`);
  };

  // Handle local mic toggle (self mute/unmute) — same action as bottom controls
  const handleToggleLocalMic = async () => {
    if (mMeeting?.localMicOn) {
      await mMeeting.muteMic();
      if (rtm?.setPresenceState) {
        rtm.setPresenceState({ micMuted: "true" });
      }
    } else {
      await mMeeting.unmuteMic();
      if (rtm?.setPresenceState) {
        rtm.setPresenceState({ micMuted: "false" });
      }
    }
  };

  // Handle local camera toggle (self on/off) — same action as bottom controls
  const handleToggleLocalCamera = async () => {
    if (mMeeting?.localWebcamOn) {
      mMeeting.disableWebcam();
      if (rtm?.setPresenceState) {
        rtm.setPresenceState({ camOff: "true" });
      }
    } else {
      mMeeting.enableWebcam();
      if (rtm?.setPresenceState) {
        rtm.setPresenceState({ camOff: "false" });
      }
    }
  };

  // Handle disabling remote participant's webcam - broadcasts to target participant
  const handleDisableWebcam = (participantId) => {
    publishDisableWebcam(
      { participantId },
      { persist: false }
    );
    console.log(`Disable webcam request sent to: ${participantId}`);
  };

  // Handle muting remote participant - uses Socket.IO (reliable, not rate-limited).
  // Now awaits an ack from the target so the host sees real success/failure.
  // If the target doesn't ack in time (flaky mobile, socket lost), we silence
  // the participant locally so the host stops hearing them even if their client
  // never received the command. Legacy targets without ack support look like a
  // timeout — same fallback applies, and the old broadcast-based mute still
  // takes effect on most clients.
  const handleMuteMic = async (participantId) => {
    console.log(`Mute mic request sent to: ${participantId} (Socket.IO)`);
    const pendingToastId = toast.info('Muting participant…', {
      position: 'bottom-right',
      autoClose: false,
      theme: 'dark',
      isLoading: true,
    });
    try {
      const result = await meetingState.emitForceMute(participantId);
      if (result?.success && result?.verified) {
        toast.update(pendingToastId, {
          render: 'Participant muted.',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      } else if (result?.timedOut) {
        // No ack — likely high-latency or dropped socket. Silence locally so
        // we stop hearing them; the broadcast may still have landed on other
        // clients, which will each apply their own silencing.
        const silenced = mMeeting?.silenceRemoteUser?.(participantId);
        toast.update(pendingToastId, {
          render: silenced
            ? "No response from participant — silenced locally."
            : "No response from participant — their mic may still be live.",
          type: 'warning',
          isLoading: false,
          autoClose: 4000,
        });
      } else {
        const silenced = mMeeting?.silenceRemoteUser?.(participantId);
        toast.update(pendingToastId, {
          render: silenced
            ? "Mute failed on participant's device — silenced locally."
            : "Mute failed on participant's device.",
          type: 'warning',
          isLoading: false,
          autoClose: 4000,
        });
      }
    } catch (err) {
      console.error('[Mute] handleMuteMic threw:', err);
      toast.update(pendingToastId, {
        render: 'Mute request failed.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  // Handle spotlight toggle — uses Socket.IO for persistence and broadcast
  const handleSpotlight = (participantId, displayName, isCurrentlySpotlighted) => {
    const action = isCurrentlySpotlighted ? 'unspotlight' : 'spotlight';
    meetingState.emitSpotlight(participantId, displayName, action);
    console.log(`Spotlight: ${action} for ${displayName} (${participantId})`);
  };

  // Handle shadow ban toggle
  // Always normalizes participant IDs to strings for consistent Set lookups
  // (Agora RTM userIds are strings; VideoSDK IDs may be numeric)
  const handleToggleShadowBan = (participantId, displayName) => {
    const strId = String(participantId);
    const isCurrentlyBanned = shadowBannedParticipants?.has?.(participantId) || shadowBannedParticipants?.has?.(strId);
    const action = isCurrentlyBanned ? 'unban' : 'ban';

    // Update local state - store as string for consistent comparison with RTM userIds
    setShadowBannedParticipants(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyBanned) {
        newSet.delete(participantId);
        newSet.delete(strId);
      } else {
        newSet.add(strId);
      }
      console.log("Shadow ban state updated:", [...newSet]);
      return newSet;
    });

    // Broadcast to ALL participants (always send string ID)
    publishShadowBan(
      JSON.stringify({
        participantId: strId,
        displayName: displayName || 'Participant',
        action
      }),
      { persist: true }
    );
  };

  // Handle co-host toggle
  const handleToggleCoHost = (participantId, displayName, isCurrentlyCoHost) => {
    const action = isCurrentlyCoHost ? 'demote' : 'promote';
    const newRole = isCurrentlyCoHost ? 'participant' : 'co-host';

    console.log("=== handleToggleCoHost called ===");
    console.log("handleToggleCoHost params:", {
      participantId,
      participantIdType: typeof participantId,
      displayName,
      isCurrentlyCoHost,
      action,
      newRole
    });

    // Update local state immediately
    if (action === 'promote') {
      addCoHost(participantId);
    } else {
      removeCoHost(participantId);
    }

    // Publish role change to all participants
    const messagePayload = {
      targetParticipantId: participantId,
      newRole,
      action,
    };
    console.log("handleToggleCoHost publishing:", messagePayload);

    publishRoleChange(messagePayload, { persist: true });

    // Persist co-host to DB for logged-in users (check target's presence for userId)
    // Try direct lookup first, then search by numeric UID (Agora UID mismatch: remote participants
    // have numeric UIDs in RTC but string IDs in RTM presence)
    let targetPresence = rtm?.presenceSnapshot?.get?.(String(participantId))?.states;
    if (!targetPresence && rtm?.presenceSnapshot) {
      const numericId = typeof participantId === 'number' ? participantId : Number(participantId);
      if (!isNaN(numericId)) {
        for (const [rtmUserId, entry] of rtm.presenceSnapshot) {
          if (stringToNumericUid(rtmUserId) === numericId) {
            targetPresence = entry?.states;
            break;
          }
        }
      }
    }
    const targetUserId = targetPresence?.userId;
    console.log("handleToggleCoHost DB persist:", { participantId, targetPresence, targetUserId, hasPresenceSnapshot: !!rtm?.presenceSnapshot, snapshotSize: rtm?.presenceSnapshot?.size });
    if (targetUserId && meetingSettings?.roomId) {
      console.log("Calling updateCoHost API:", { meetingId: meetingSettings.roomId, targetUserId, action });
      meetingServices.updateCoHost({
        meetingId: meetingSettings.roomId,
        targetUserId,
        action,
      }).catch((err) => {
        console.error('Failed to persist co-host to DB:', err);
      });
    }

    console.log(`Co-host ${action} for:`, displayName, participantId);
  };

  // Handle kick/remove participant from meeting
  const handleKickParticipant = (participantId, displayName) => {
    console.log(`Kicking participant: ${displayName} (${participantId})`);
    publishKickParticipant(
      JSON.stringify({
        targetParticipantId: participantId,
        displayName: displayName || 'Participant',
        kickedBy: localParticipant?.displayName || 'Host',
      }),
      { persist: false }
    );
  };

  // Handle lower all hands
  const handleLowerAllHands = () => {
    if (onLowerAllHands) {
      onLowerAllHands();
    }
  };

  // Get raised hands from context
  const { raisedHandsParticipants } = useMeetingAppContext();

  // Create a Map of participant IDs to raisedHandOn timestamps for quick lookup and FIFO ordering
  const raisedHandsMap = useMemo(() => {
    return new Map(raisedHandsParticipants.map(p => [p.participantId, p.raisedHandOn]));
  }, [raisedHandsParticipants]);

  // Convert participants Map to array
  const participantsList = useMemo(() => {
    if (!participants) return [];
    const list = [];

    // Add local participant first
    if (localParticipant) {
      list.push({
        id: localParticipant.id,
        isLocal: true,
      });
    }

    // Add remote participants
    participants.forEach((participant, id) => {
      if (id !== localParticipant?.id) {
        list.push({
          id: id,
          isLocal: false,
        });
      }
    });

    return list;
  }, [participants, localParticipant]);

  // Helper: check participant role
  const getParticipantRoleCategory = React.useCallback((p) => {
    const numericId = typeof p.id === 'number' ? p.id : stringToNumericUid(p.id);
    const isParticipantPresenter = presenters?.has(p.id) ||
                                    presenters?.has(String(p.id)) ||
                                    presenters?.has(numericId);
    if (p.isLocal) {
      if (['moderator', 'host'].includes(meetingRole)) return 'host';
      if (meetingRole === 'co-host') return 'co-host';
      if (meetingRole === 'presenter' || meetingRole === 'speaker' || isParticipantPresenter) return 'presenter';
      return 'attendee';
    }
    const participant = participants?.get(p.id);
    const participantRole = participant?.metaData?.meetingRole;
    if (participantRole === 'moderator' || participantRole === 'host') return 'host';
    if (isCoHost(p.id) || (participantRole === 'co-host' && !wasDemotedCoHost(p.id))) return 'co-host';
    if (participantRole === 'presenter' || participantRole === 'speaker' || isParticipantPresenter) return 'presenter';
    return 'attendee';
  }, [meetingRole, isCoHost, wasDemotedCoHost, participants, presenters]);

  // Group by role - raised hands section + role sections (participants stay in their role section too)
  const groupedParticipants = useMemo(() => {
    const raisedHands = participantsList
      .filter(p => raisedHandsMap.has(p.id))
      .sort((a, b) => (raisedHandsMap.get(a.id) || 0) - (raisedHandsMap.get(b.id) || 0));

    const hosts = participantsList
      .filter(p => {
        const role = getParticipantRoleCategory(p);
        return role === 'host' || role === 'co-host';
      })
      .sort((a, b) => {
        const roleOrder = { host: 0, 'co-host': 1 };
        return (roleOrder[getParticipantRoleCategory(a)] || 0) - (roleOrder[getParticipantRoleCategory(b)] || 0);
      });

    const presentersGroup = participantsList
      .filter(p => getParticipantRoleCategory(p) === 'presenter');

    const attendees = participantsList
      .filter(p => getParticipantRoleCategory(p) === 'attendee');

    return { raisedHands, hosts, presenters: presentersGroup, attendees };
  }, [participantsList, raisedHandsMap, getParticipantRoleCategory]);

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  // Category-based filter keys from assigned categories
  const categoryFilterKeys = useMemo(() => {
    return categories.map(cat => `cat_${cat.id}`);
  }, [categories]);

  const activeCategoryFilters = useMemo(() => {
    return categories.filter(cat => filters[`cat_${cat.id}`]);
  }, [categories, filters]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handleClick = (e) => {
      if (filterBtnRef.current?.contains(e.target)) return;
      if (filterDropdownRef.current?.contains(e.target)) return;
      setShowFilterDropdown(false);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handleClick), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handleClick); };
  }, [showFilterDropdown]);

  const handleFilterBtnClick = (e) => {
    if (showFilterDropdown) {
      setShowFilterDropdown(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const dropdownWidth = 220;
      let left = rect.right - dropdownWidth;
      if (left < 8) left = 8;
      setFilterDropdownPosition({ top: rect.bottom + 4, left });
      setShowFilterDropdown(true);
    }
  };

  const clearAllFilters = () => {
    const cleared = {};
    Object.keys(filters).forEach(k => { cleared[k] = false; });
    setFilters(cleared);
  };

  const renderSection = (title, sectionParticipants, sectionKey, icon, iconColor = 'text-gray-400') => {
    if (sectionParticipants.length === 0) return null;
    const isExpanded = expandedSections.includes(sectionKey);
    const Icon = icon;

    return (
      <div key={sectionKey} className="mb-2">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between px-2 py-2 hover:bg-white/5 rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
            <span className="text-sm font-medium text-white">{title}</span>
            <span className="text-xs text-gray-500">({sectionParticipants.length})</span>
          </div>
        </button>

        {(isExpanded || searchTerm) && (
          <div className="space-y-1 mt-1">
            {sectionParticipants.map(participant => (
              <FilterableParticipant
                key={participant.id}
                participantId={participant.id}
                isLocal={participant.isLocal}
                localParticipant={localParticipant}
                hasRaisedHand={raisedHandsMap.has(participant.id)}
                onLowerHand={onLowerHand}
                onRequestUnmute={handleRequestUnmute}
                onMakePresenter={handleMakePresenter}
                onToggleShadowBan={handleToggleShadowBan}
                isShadowBanned={shadowBannedParticipants?.has?.(participant.id) || shadowBannedParticipants?.has?.(String(participant.id))}
                isCoHost={isCoHost(participant.id)}
                onToggleCoHost={handleToggleCoHost}
                isHostUser={isHostUser}
                localUserRole={meetingRole}
                filters={filters}
                searchTerm={searchTerm}
                onStatusReport={handleStatusReport}
                onDisableWebcam={handleDisableWebcam}
                onMuteMic={handleMuteMic}
                onKickParticipant={handleKickParticipant}
                isPresenter={(() => {
                  // Check all ID formats for presenter status (numeric UID, string, original)
                  const numericId = typeof participant.id === 'number' ? participant.id : stringToNumericUid(participant.id);
                  return presenters?.has(participant.id) ||
                         presenters?.has(String(participant.id)) ||
                         presenters?.has(numericId);
                })()}
                onSpotlight={handleSpotlight}
                isSpotlighted={meetingState.spotlightedParticipants?.some(
                  s => s.participantId === participant.id || s.participantId === String(participant.id)
                )}
                onToggleLocalMic={handleToggleLocalMic}
                onToggleLocalCamera={handleToggleLocalCamera}
                categories={categories}
                participantCategory={(() => {
                  // Check all ID formats (string participantId, numeric Agora UID)
                  const numId = typeof participant.id === 'number' ? participant.id : stringToNumericUid(participant.id);
                  return categoryAssignments[participant.id]
                    || categoryAssignments[String(participant.id)]
                    || categoryAssignments[numId]
                    || categoryAssignments[String(numId)]
                    || null;
                })()}
                onAssignCategory={handleAssignCategory}
                roleCategory={getParticipantRoleCategory(participant)}
                networkQuality={(() => {
                  if (!networkQualities) return null;
                  // Local participant uses 'local' key
                  if (participant.isLocal) return networkQualities.get('local') || null;
                  // Remote: try numeric UID and string forms
                  return networkQualities.get(participant.id)
                    || networkQualities.get(Number(participant.id))
                    || networkQualities.get(String(participant.id))
                    || null;
                })()}
                canSpotlight={canSpotlight}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Compact Header with Search */}
      <div className="p-2 border-b border-white/10">
        <div className="flex gap-2 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full h-8 pl-8 text-sm bg-white/5 border border-white/10 rounded-md text-white placeholder:text-gray-500 focus:outline-none focus:border-[#6a1fbf]/50"
            />
          </div>

          <button
            ref={filterBtnRef}
            onClick={handleFilterBtnClick}
            className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors relative ${
              showFilterDropdown || activeFiltersCount > 0 ? 'bg-white/10 border-white/20 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
            }`}
            title="Filter participants"
          >
            <Filter className="w-3.5 h-3.5" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#6a1fbf] rounded-full text-[9px] text-white flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {/* Roles & Categories Manager */}
          {isHostUser && (
            <button
              onClick={() => setShowRolesManager(true)}
              className="h-8 w-8 flex items-center justify-center rounded-md border bg-white/5 border-white/10 text-purple-400 hover:bg-white/10 transition-colors"
              title="Manage Roles & Categories"
            >
              <Shield className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            className="h-8 w-8 flex items-center justify-center rounded-md bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:shadow-lg hover:shadow-[#6a1fbf]/25 transition-all"
            title="Invite participants"
            onClick={() => {
              const meetingLink = window.location.href;
              navigator.clipboard.writeText(meetingLink).then(() => {
                toast.success('Meeting link copied to clipboard!');
              }).catch(() => {});
            }}
          >
            <UserPlus className="w-3.5 h-3.5 text-white" />
          </button>
        </div>

        {/* Active filter pills (shown inline when filters are active) */}
        {activeFiltersCount > 0 && (
          <div className="mt-2 flex flex-wrap gap-1 items-center">
            {filters.micOn && <span className="px-1.5 py-0.5 rounded bg-[#6a1fbf]/20 text-[#6a1fbf] text-[10px] flex items-center gap-1"><Mic className="w-2.5 h-2.5" />Mic on</span>}
            {filters.micOff && <span className="px-1.5 py-0.5 rounded bg-[#6a1fbf]/20 text-[#6a1fbf] text-[10px] flex items-center gap-1"><MicOff className="w-2.5 h-2.5" />Mic off</span>}
            {filters.cameraOn && <span className="px-1.5 py-0.5 rounded bg-[#6a1fbf]/20 text-[#6a1fbf] text-[10px] flex items-center gap-1"><Video className="w-2.5 h-2.5" />Cam on</span>}
            {filters.cameraOff && <span className="px-1.5 py-0.5 rounded bg-[#6a1fbf]/20 text-[#6a1fbf] text-[10px] flex items-center gap-1"><VideoOff className="w-2.5 h-2.5" />Cam off</span>}
            {filters.raisedHand && <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-[10px] flex items-center gap-1"><Hand className="w-2.5 h-2.5" />Hand</span>}
            {filters.hostsOnly && <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-[10px] flex items-center gap-1"><Crown className="w-2.5 h-2.5" />Hosts</span>}
            {filters.presentersOnly && <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 text-[10px] flex items-center gap-1"><Star className="w-2.5 h-2.5" />Presenters</span>}
            {filters.attendeesOnly && <span className="px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400 text-[10px] flex items-center gap-1"><Users className="w-2.5 h-2.5" />Attendees</span>}
            {activeCategoryFilters.map(cat => {
              const CatIcon = CATEGORY_ICONS[cat.icon] || Crown;
              return <span key={cat.id} className="px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1" style={{ backgroundColor: `${cat.color}20`, color: cat.color }}><CatIcon className="w-2.5 h-2.5" />{cat.name}</span>;
            })}
            <button onClick={clearAllFilters} className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-white transition-colors">
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}

        {/* Inline count and quick actions */}
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="text-gray-400">{participantsList.length} participants</span>
          {isHostUser && (
            <div className="flex items-center gap-1">
              {/* Mute All / Unmute All toggle */}
              {meetingState.hostControls?.muteAll ? (
                <button
                  onClick={() => {
                    if (onUnmuteAll) {
                      onUnmuteAll();
                    } else {
                      meetingState.emitGrantUnmuteAll();
                    }
                  }}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title="Unmute all (currently muted)"
                >
                  <MicOff className="w-3.5 h-3.5 text-red-400" />
                </button>
              ) : (
                <button
                  onClick={handleMuteAll}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title="Mute all"
                >
                  <Mic className="w-3.5 h-3.5 text-green-400" />
                </button>
              )}

              {/* Disable All Cameras */}
              <button
                onClick={() => {
                  if (!participants) return;
                  const remoteIds = [...participants.keys()].filter(id => id !== localParticipant?.id);
                  remoteIds.forEach(id => handleDisableWebcam(id));
                  toast.info('Disabled all cameras');
                }}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
                title="Turn off all cameras"
              >
                <VideoOff className="w-3.5 h-3.5 text-gray-400" />
              </button>

              {/* Broadcast Message */}
              <button
                onClick={() => setShowBroadcastModal(true)}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
                title="Broadcast message"
              >
                <Megaphone className="w-3.5 h-3.5 text-purple-400" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Participants List */}
      <div className="flex-1 overflow-y-auto p-2">
        {participantsList.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p>No participants yet</p>
            <p className="text-xs mt-1">Invite people to join</p>
          </div>
        ) : (
          <>
            {/* Waiting Room Section */}
            <WaitingRoomSection
              waitingParticipants={waitingParticipants}
              onAdmit={onAdmitParticipant}
              onAdmitAll={onAdmitAllParticipants}
              onDeny={onDenyParticipant}
              isExpanded={expandedSections.includes('waitingRoom')}
              onToggle={() => toggleSection('waitingRoom')}
              onSendMessageToWaiting={onSendMessageToWaiting}
              waitingRoomChatMessages={waitingRoomChatMessages}
              autoAdmitRegisteredInWaitingRoom={autoAdmitRegisteredInWaitingRoom}
            />

            {/* Raised Hands - styled like prototype with yellow bg */}
            {groupedParticipants.raisedHands.length > 0 && (
              <div className="mb-2 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                <button
                  onClick={() => toggleSection('raisedHands')}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-yellow-500/10 rounded-t-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {expandedSections.includes('raisedHands') ? (
                      <ChevronDown className="w-3.5 h-3.5 text-yellow-400" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-yellow-400" />
                    )}
                    <Hand className="w-3.5 h-3.5 text-yellow-400" />
                    <span className="text-xs font-medium text-yellow-400">Raised Hands ({groupedParticipants.raisedHands.length})</span>
                  </div>
                  {isHostUser && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleLowerAllHands(); }}
                      className="text-[10px] text-yellow-400 hover:text-yellow-300 px-1.5 py-0.5 hover:bg-yellow-500/20 rounded"
                    >
                      Lower All
                    </button>
                  )}
                </button>

                {expandedSections.includes('raisedHands') && (
                  <div className="px-2 pb-2 space-y-1">
                    {groupedParticipants.raisedHands.map((participant, index) => {
                      const raisedAt = raisedHandsMap.get(participant.id);
                      const timeSince = raisedAt ? (() => {
                        const seconds = Math.floor((Date.now() - new Date(raisedAt).getTime()) / 1000);
                        if (seconds < 60) return `${seconds}s`;
                        return `${Math.floor(seconds / 60)}m`;
                      })() : '';
                      return (
                        <RaisedHandItem
                          key={participant.id}
                          participantId={participant.id}
                          isLocal={participant.isLocal}
                          localParticipant={localParticipant}
                          index={index}
                          timeSince={timeSince}
                          isHostUser={isHostUser}
                          onLowerHand={onLowerHand}
                          onSpotlight={handleSpotlight}
                          isSpotlighted={meetingState.spotlightedParticipants?.some(
                            s => s.participantId === participant.id || s.participantId === String(participant.id)
                          )}
                          onMakePresenter={handleMakePresenter}
                          canSpotlight={canSpotlight}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {renderSection('Hosts & Co-Hosts', groupedParticipants.hosts, 'hosts', Crown)}
            {renderSection('Presenters & Panelists', groupedParticipants.presenters, 'presenters', Star, 'text-green-400')}
            {renderSection('Attendees', groupedParticipants.attendees, 'attendees', Users)}
          </>
        )}
      </div>

      {/* Broadcast Modal */}
      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        onSend={({ message, target }) => {
          meetingState.emitBroadcastMessage(message, target);
          setShowBroadcastModal(false);
          toast.success('Broadcast sent!');
        }}
      />

      {/* Roles & Categories Manager Modal */}
      <RolesAndCategoriesModal
        isOpen={showRolesManager}
        onClose={() => setShowRolesManager(false)}
        roles={roles}
        categories={categories}
        onAddRole={addRole}
        onDeleteRole={deleteRole}
        onAddCategory={addCategory}
        onDeleteCategory={deleteCategory}
      />

      {/* Filter Dropdown - Portal */}
      {showFilterDropdown && createPortal(
        <div
          ref={filterDropdownRef}
          className="fixed bg-[#001233] rounded-lg shadow-2xl border border-white/10 py-1 w-[220px] max-h-[400px] overflow-y-auto"
          style={{ top: filterDropdownPosition.top, left: filterDropdownPosition.left, zIndex: 99999 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-[11px] font-medium text-gray-400">Filter by</span>
            {activeFiltersCount > 0 && (
              <button onClick={clearAllFilters} className="text-[10px] text-[#6a1fbf] hover:underline">
                Clear all
              </button>
            )}
          </div>
          <div className="border-t border-white/10 my-0.5" />

          {/* Media filters */}
          <FilterCheckItem label="Mic on" icon={<Mic className="w-3 h-3" />} checked={filters.micOn} onChange={(v) => setFilters(f => ({ ...f, micOn: v }))} />
          <FilterCheckItem label="Mic off" icon={<MicOff className="w-3 h-3" />} checked={filters.micOff} onChange={(v) => setFilters(f => ({ ...f, micOff: v }))} />
          <FilterCheckItem label="Camera on" icon={<Video className="w-3 h-3" />} checked={filters.cameraOn} onChange={(v) => setFilters(f => ({ ...f, cameraOn: v }))} />
          <FilterCheckItem label="Camera off" icon={<VideoOff className="w-3 h-3" />} checked={filters.cameraOff} onChange={(v) => setFilters(f => ({ ...f, cameraOff: v }))} />
          <FilterCheckItem label="Hand raised" icon={<Hand className="w-3 h-3" />} checked={filters.raisedHand} onChange={(v) => setFilters(f => ({ ...f, raisedHand: v }))} />

          {/* Role filters */}
          <div className="border-t border-white/10 my-0.5" />
          <div className="px-3 py-1.5">
            <span className="text-[11px] font-medium text-gray-400">By Role</span>
          </div>
          <FilterCheckItem label="Hosts only" icon={<Crown className="w-3 h-3 text-yellow-400" />} checked={filters.hostsOnly} onChange={(v) => setFilters(f => ({ ...f, hostsOnly: v }))} />
          <FilterCheckItem label="Presenters only" icon={<Star className="w-3 h-3 text-blue-400" />} checked={filters.presentersOnly} onChange={(v) => setFilters(f => ({ ...f, presentersOnly: v }))} />
          <FilterCheckItem label="Attendees only" icon={<Users className="w-3 h-3 text-gray-400" />} checked={filters.attendeesOnly} onChange={(v) => setFilters(f => ({ ...f, attendeesOnly: v }))} />

          {/* Category filters */}
          {categories.length > 0 && (
            <>
              <div className="border-t border-white/10 my-0.5" />
              <div className="px-3 py-1.5">
                <span className="text-[11px] font-medium text-gray-400">By Category</span>
              </div>
              {categories.map(cat => {
                const CatIcon = CATEGORY_ICONS[cat.icon] || Crown;
                const filterKey = `cat_${cat.id}`;
                return (
                  <FilterCheckItem
                    key={cat.id}
                    label={`${cat.name} only`}
                    icon={<CatIcon className="w-3 h-3" style={{ color: cat.color }} />}
                    checked={!!filters[filterKey]}
                    onChange={(v) => setFilters(f => ({ ...f, [filterKey]: v }))}
                  />
                );
              })}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
